import type { renditions, TrackingPropsInterface } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import type { DcaSlotTarget, SlotContent } from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';
import { buildCardTrackingDescription, resolveCardCategory, resolveCardDescription } from './polarisTrackingUtils';

export type SlotTrackingContext = {
  index: number;
  tabIndex: number;
  tabName: string;
  slotSize: 'square' | 'tall' | 'banner';
  trackingProperties?: TrackingPropsInterface & {
    tabTrackingContentPosition?: string;
  };
};

export type SlotResolveContext = {
  slot: SlotContent;
  trackingCtx?: SlotTrackingContext;
};

export const EMPTY_RENDITIONS: renditions = { altText: '', assetPath: '', dynamic: false, renditions: [] };
export const DEFAULT_DAC_IMAGE_DOMAIN = 'https://cache.marriott.com';

export function isRecord(obj: unknown): obj is Record<string, unknown> {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
}

export function getContentObj(slot: SlotContent): Record<string, unknown> {
  return (slot?.payload && typeof slot?.payload === 'object' ? slot.payload : {}) as Record<string, unknown>;
}

export function isAbsoluteUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//');
}

export function getDacImageDomain(): string {
  const envVars = useClientEnvVarsStore.getState?.().envVarsObject as Record<string, unknown> | undefined;
  const configuredDomain = typeof envVars?.['DAC_IMG_DOMAIN'] === 'string' ? envVars['DAC_IMG_DOMAIN'].trim() : '';
  return configuredDomain || DEFAULT_DAC_IMAGE_DOMAIN;
}

export function resolveImageUrl(url: string): string {
  const normalizedUrl = url?.trim();
  if (!normalizedUrl || isAbsoluteUrl(normalizedUrl)) return normalizedUrl;
  const imageDomain = getDacImageDomain().replace(/\/+$/, '');
  return `${imageDomain}${normalizedUrl.startsWith('/') ? normalizedUrl : `/${normalizedUrl}`}`;
}

export function resolveBrandLogoIcon(c: Record<string, unknown>): string {
  const rawBrandLogoIcon = c['brandLogoIcon'];
  if (typeof rawBrandLogoIcon === 'string' && rawBrandLogoIcon) return rawBrandLogoIcon;
  if (Array.isArray(rawBrandLogoIcon) && typeof rawBrandLogoIcon[0] === 'string' && rawBrandLogoIcon[0]) {
    return rawBrandLogoIcon[0];
  }

  const logoObj = isRecord(c['logo']) ? c['logo'] : null;
  const logoImageSrc = logoObj && typeof logoObj['imageSrc'] === 'string' ? logoObj['imageSrc'] : '';
  if (!logoImageSrc) return '';
  // Added the following check if the logo image src is already a brand logo icon
  if (logoImageSrc.startsWith('mcom-common:brands/')) return logoImageSrc;

  const brandCodeMatch = logoImageSrc.match(/\/brand-logos\/([^/.]+)(?:\.[a-zA-Z0-9]+)?$/i);
  if (brandCodeMatch?.[1]) return `mcom-common:brands/${brandCodeMatch[1]}`;

  return resolveImageUrl(logoImageSrc);
}

export type DcaTrackingValues = {
  trackingContentPosition?: string;
  trackingOfferType?: string;
  trackingDescription?: string;
  trackingTag?: string;
};

export function parseDcaImpressionValue(value: unknown): DcaTrackingValues {
  if (typeof value !== 'string') return {};
  const [positionPart, ...trackingParts] = value.split(',');
  const parsed: DcaTrackingValues = {
    trackingContentPosition: positionPart?.trim() || undefined,
  };

  trackingParts.forEach(part => {
    const [rawKey, ...rawValueParts] = part.split('=');
    const key = rawKey?.trim().toLowerCase();
    const parsedValue = rawValueParts.join('=').trim();
    if (!key || !parsedValue) return;

    if (key === 'type') parsed.trackingOfferType = parsedValue;
    if (key === 'msg') parsed.trackingDescription = parsedValue;
    if (key === 'tag') parsed.trackingTag = parsedValue;
  });

  return parsed;
}

/** Strip the literal `-mbox` suffix so tracking uses the placement position, not the raw mbox name. */
export function stripMboxSuffix(name: string): string {
  return name.replace(/-mbox$/i, '');
}

export function resolveDcaMboxTrackingPosition(c: Record<string, unknown>): string {
  const mboxValue =
    typeof c['mboxName'] === 'string'
      ? c['mboxName']
      : typeof c['mboxParameter'] === 'string'
      ? c['mboxParameter']
      : '';
  return stripMboxSuffix(mboxValue.trim());
}

export function getDcaFallbackTrackingTarget(slot?: SlotContent): DcaSlotTarget | undefined {
  return slot?.type === 'cf' ? slot.dcaFallbackTarget : undefined;
}

export function getDcaFallbackTrackingPosition(slot?: SlotContent): string {
  const target = getDcaFallbackTrackingTarget(slot);
  return typeof target?.mboxParameter === 'string' ? stripMboxSuffix(target.mboxParameter.trim()) : '';
}

export function buildDcaFallbackList3Entry(slot?: SlotContent): string | null {
  const position = getDcaFallbackTrackingPosition(slot);
  return position ? `${position},msg=dca,type=,tag=undefined` : null;
}

export function buildDcaFallbackTrackingProps(slot?: SlotContent): TrackingPropsInterface | null {
  const position = getDcaFallbackTrackingPosition(slot);
  if (!position) return null;

  return {
    trackingContentPosition: position,
    trackingDescription: 'dca',
    trackingOfferType: '',
    trackingTag: 'undefined',
    atCCeVar48: '',
    clickTrack: true,
    impressionTrack: true,
    impressionCount: true,
    isDCAEnabled: true,
    // Match normal DCA: the separator keeps the per-card CTA Button from firing its own
    // event168|list3 impression (Button only fires it when impressionTrack && !clickTrackValSeparator),
    // while still writing the list3 CLICK value — so the DTL batch is the single impression source.
    clickTrackValSeparator: ';',
  } as TrackingPropsInterface;
}

export function trackingProps(
  c: Record<string, unknown>,
  trackingCtx?: SlotTrackingContext,
  slotType?: 'cf' | 'dca' | string,
  slot?: SlotContent
): TrackingPropsInterface {
  const fallbackTrackingProps = buildDcaFallbackTrackingProps(slot);
  if (fallbackTrackingProps) return fallbackTrackingProps;

  const base = (trackingCtx?.trackingProperties ?? {}) as Record<string, unknown>;
  const metadata =
    slotType === 'dca' ? ((c['metaData'] ?? c['metadata']) as Record<string, unknown> | undefined) : undefined;
  const evar48 = ((metadata?.['evar48'] || c['atCCeVar48'] || c['eVar48'] || '') as string).trim();
  const tabTrackingContentPosition =
    typeof base['tabTrackingContentPosition'] === 'string' ? (base['tabTrackingContentPosition'] as string) : '';
  const category = resolveCardCategory(c, slotType);
  const description = resolveCardDescription(c, slotType);
  const cardTrackingDesc = buildCardTrackingDescription({
    tabIndex: trackingCtx?.tabIndex ?? 1,
    tabName: trackingCtx?.tabName ?? '',
    cardNumber: trackingCtx?.index ?? 1,
    shape: trackingCtx?.slotSize ?? 'square',
    category,
    description,
  });
  return {
    ...base,
    trackingContentPosition:
      slotType === 'dca'
        ? metadata
          ? 'dca'
          : resolveDcaMboxTrackingPosition(c) || (c['trackingContentPosition'] as string)
        : tabTrackingContentPosition || base['trackingContentPosition'] || (c['trackingContentPosition'] as string),
    trackingDescription:
      slotType === 'dca'
        ? metadata
          ? 'dca'
          : (c['trackingDescription'] as string) || (metadata?.['title'] as unknown as string)
        : cardTrackingDesc,
    trackingOfferType:
      slotType === 'dca'
        ? metadata
          ? 'dca'
          : (c['trackingOfferType'] as string) || (metadata?.['type'] as unknown as string)
        : base['trackingOfferType'],
    trackingTag:
      slotType === 'dca'
        ? metadata
          ? 'dca'
          : (c['trackingTag'] as string) ||
            (metadata?.['merTag'] as unknown as string) ||
            (metadata?.['offerCode'] as unknown as string)
        : base['trackingTag'],
    ...(slotType === 'dca' && evar48 && { atCCeVar48: evar48 }),
    clickTrack: slotType === 'dca' ? 'true' : c['clickTrack'] ?? base['clickTrack'],
    impressionTrack: slotType === 'dca' ? 'true' : c['impressionTrack'] ?? base['impressionTrack'],
    impressionCount: slotType === 'dca' ? true : '',
    isDCAEnabled: slotType === 'dca' ? true : '',
    enableScrollingBehavior: c['enableScrollingBehavior'] ?? base['enableScrollingBehavior'],
  } as TrackingPropsInterface;
}

export const isExternalLink = (linkHref: string, openInaNewTab: boolean) => {
  try {
    if (typeof window !== 'undefined' && window.document) {
      const currentDomain = window?.location?.hostname;
      const urlDomain = new URL(linkHref)?.hostname;
      return currentDomain !== urlDomain;
    } else {
      return openInaNewTab === true; // check for evergreen content on server side
    }
  } catch {
    return false;
  }
};
